import axios from 'axios';
import * as Sentry from '@sentry/vue';
import qs from 'qs';
import { i18n } from '../i18n/i18n';
import Storage from '../Storage';
import errorService, { ApiError } from './errorService';

// Konfiguracja cache
const CACHE_LIFETIME = 1000 * 60 * 60; // 60 minut
const CACHE_CAPACITY = 1000;
const requestCache = new Map();

const isTestEnv = process.env.NODE_ENV === 'test';
export const appBaseUrl = isTestEnv ? 'http://localhost' : process.env.VUE_APP_API_URL; // Ustawiamy 'http://localhost' dla środowiska testowego

// Ustawienia axios
axios.defaults.timeout = 1000 * 60 * 5; // 5 minut
axios.defaults.paramsSerializer = params => qs.stringify(params, { arrayFormat: 'brackets' });

// Globalny AbortController (dla żądań, które nie mają indywidualnego kontrolera)
let abortController = new AbortController();

// Rejestr indywidualnych AbortControllerów – umożliwia globalny abort również dla żądań z własnym kontrolerem
const individualControllers = new Set();

/**
 * Interceptor przypisujący sygnał do konfiguracji żądania.
 * Jeśli nie został dostarczony indywidualny sygnał, używa globalnego.
 * Jeśli w konfiguracji znajduje się indywidualAbortController, rejestruje go w centralnym rejestrze.
 */
const abortControllerInterceptor = config => {
    if (!config.signal) {
        // Używamy globalnego AbortControllera
        config.signal = abortController.signal; // eslint-disable-line no-param-reassign
    } else if (config.individualAbortController) {
        // Rejestrujemy indywidualny AbortController, aby móc go później anulować globalnie
        individualControllers.add(config.individualAbortController);
    }
    return config;
};

// Interceptor do łączenia parametrów tablicowych
const arrayParamsInterceptor = config => {
    if (config.params && typeof config.params === 'object') {
        Object.keys(config.params).forEach(key => {
            if (Array.isArray(config.params[key])) {
                // Zamiana tablicy na ciąg znaków oddzielony przecinkami
                // eslint-disable-next-line no-param-reassign
                config.params[key] = config.params[key].join(',');
            }
        });
    }
    return config;
};

// Funkcja sprawdzająca, czy URL jest absolutny
const isAbsoluteURL = url => /^([a-z][a-z\d+\-.]*:)?\/\//i.test(url);

// Funkcja budująca pełny URL
const buildFullURL = (baseURL, requestedURL) => {
    if (baseURL && !isAbsoluteURL(requestedURL)) {
        return `${baseURL.replace(/\/+$/, '')}/${requestedURL.replace(/^\/+/, '')}`;
    }
    return requestedURL;
};

const cacheRequestInterceptor = config => {
    // Jeśli cache jest wyłączone dla tego żądania, pomiń cache
    if (config.cache !== true) return config;

    let fullURL = buildFullURL(config.baseURL, config.url);

    if (config.params) {
        const queryString = qs.stringify(config.params, { arrayFormat: 'repeat' });
        fullURL += `?${queryString}`;
    }

    const cacheKey = fullURL;
    const cachedData = requestCache.get(cacheKey);

    // Sprawdzenie, czy odpowiedź jest w cache i nie jest przeterminowana
    if (cachedData && Date.now() - cachedData.timestamp < CACHE_LIFETIME) {
        // Zwraca odpowiedź z cache jako symulowany request
        // eslint-disable-next-line no-param-reassign
        config.adapter = () => Promise.resolve(cachedData.response);
    }
    return config;
};

const cacheResponseInterceptor = response => {
    const { config } = response;
    // Jeśli cache jest wyłączone dla tego żądania, pomiń zapis do cache
    if (config.cache !== true) return response;

    let fullURL = buildFullURL(config.baseURL, config.url);
    if (config.params) {
        const queryString = qs.stringify(config.params, { arrayFormat: 'repeat' });
        fullURL += `?${queryString}`;
    }

    const cacheKey = fullURL;
    // Czyszczenie cache, jeśli przekroczona jest pojemność
    if (requestCache.size >= CACHE_CAPACITY) {
        const oldestKey = requestCache.keys().next().value;
        requestCache.delete(oldestKey);
    }

    // Zapisanie odpowiedzi w cache z timestampem
    requestCache.set(cacheKey, {
        response,
        timestamp: Date.now(),
    });

    return response;
};

/**
 * Interceptor czyszczący rejestr indywidualnych AbortControllerów po zakończeniu żądania (sukces lub błąd)
 */
const cleanupIndividualControllerInterceptor = response => {
    if (response.config.individualAbortController) {
        individualControllers.delete(response.config.individualAbortController);
    }
    return response;
};

const errorCleanupInterceptor = error => {
    if (error.config && error.config.individualAbortController) {
        individualControllers.delete(error.config.individualAbortController);
    }
    return Promise.reject(error);
};

const axiosInstance = axios.create({
    baseURL: appBaseUrl,
});

// Rejestracja interceptorów – zachowana kolejność
axiosInstance.interceptors.request.use(abortControllerInterceptor);
axiosInstance.interceptors.request.use(arrayParamsInterceptor);
axiosInstance.interceptors.request.use(cacheRequestInterceptor);
axiosInstance.interceptors.response.use(cacheResponseInterceptor);
axiosInstance.interceptors.response.use(cleanupIndividualControllerInterceptor, errorCleanupInterceptor);

export const deleteFromCache = url => {
    const urls = Array.isArray(url) ? url : [url];
    const keysToDelete = [];
    requestCache.forEach((_, key) => {
        if (key && urls.some(u => key.match(new RegExp(u)))) {
            keysToDelete.push(key);
        }
    });
    keysToDelete.forEach(key => requestCache.delete(key));
};

export const clearCache = () => {
    requestCache.clear();
};

export { axiosInstance };

export default {
    axiosInstance,
    /**
     * Metoda globalna anulująca żądania.
     * Anuluje żądania korzystające z globalnego AbortControllera
     * oraz te, które mają indywidualne kontrolery.
     */
    abortApiRequests() {
        // Anuluj globalne żądania
        abortController.abort();
        // Reset globalnego AbortControllera dla przyszłych żądań
        abortController = new AbortController();
        // Anuluj wszystkie zarejestrowane indywidualne żądania
        for (const controller of individualControllers) {
            controller.abort();
        }
        individualControllers.clear();
    },

    async handleError(e) {
        // Obsługa anulowanych żądań
        if (axios.isCancel(e) || e.code === 'ERR_CANCELED') {
            return;
        }

        if (e.response?.status >= 500) {
            Sentry.captureException(e);
        }

        if (e.response && e.response.status === 401) {
            if (e.response.config.url.includes('login-as')) {
                throw new Error(i18n.t('error.unauthorized'));
            } else {
                if (!window.IS_TEST) { // eslint-disable-line
                    const store = await import(/* @vite-ignore */ '../store/store')
                        .then(data => data.default)
                        .catch(() => {});
                    if (store.getters['auth/isAuthenticated']) store.dispatch('auth/logout');
                }
            }
        }

        const formattedError = errorService.getFormattedError(e);
        throw new ApiError('ApiError', formattedError.statusCode, formattedError.message, formattedError.errorCode, formattedError.messages, formattedError.error);
    },

    authHeader(paramHeaders = {}) {
        const headers = { ...paramHeaders };
        if (paramHeaders?.withAuth !== false) {
            const accessToken = window.localStorage.getItem(Storage.ACCESS_TOKEN);
            if (accessToken) {
                headers.Authorization = `Bearer ${accessToken}`;
            }
        }
        delete headers.withAuth;
        return headers;
    },

    /**
     * Metoda GET z opcjonalnym indywidualnym AbortControllerem.
     * Jeśli przekazany zostanie parametr customAbortController, zostanie on wykorzystany do ustawienia sygnału.
     */
    async get(url, data, headerOptions, responseType, cache = true, customAbortController) { // eslint-disable-line
        try {
            return await axiosInstance.get(url, {
                params: data,
                headers: this.authHeader(headerOptions),
                responseType,
                cache,
                ...(customAbortController ? { signal: customAbortController.signal, individualAbortController: customAbortController } : {}),
            });
        } catch (e) {
            return this.handleError(e);
        }
    },

    /**
     * Metoda POST z opcjonalnym indywidualnym AbortControllerem.
     */
    async post(url, data, headerOptions, customAbortController) {
        try {
            return await axiosInstance.post(url, data, {
                headers: this.authHeader(headerOptions),
                ...(customAbortController ? { signal: customAbortController.signal, individualAbortController: customAbortController } : {}),
            });
        } catch (e) {
            return this.handleError(e);
        }
    },

    /**
     * Metoda PUT z opcjonalnym indywidualnym AbortControllerem.
     */
    async put(url, data, customAbortController) {
        try {
            return await axiosInstance.put(url, data, {
                headers: this.authHeader(),
                ...(customAbortController ? { signal: customAbortController.signal, individualAbortController: customAbortController } : {}),
            });
        } catch (e) {
            return this.handleError(e);
        }
    },

    /**
     * Metoda PATCH z opcjonalnym indywidualnym AbortControllerem.
     */
    async patch(url, data, customAbortController) {
        try {
            return await axiosInstance.patch(url, data, {
                headers: this.authHeader(),
                ...(customAbortController ? { signal: customAbortController.signal, individualAbortController: customAbortController } : {}),
            });
        } catch (e) {
            return this.handleError(e);
        }
    },

    /**
     * Metoda DELETE z opcjonalnym indywidualnym AbortControllerem.
     */
    async delete(url, data, customAbortController) {
        try {
            return await axiosInstance.delete(url, {
                data,
                headers: this.authHeader(),
                ...(customAbortController ? { signal: customAbortController.signal, individualAbortController: customAbortController } : {}),
            });
        } catch (e) {
            return this.handleError(e);
        }
    },
};
